import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RankedPhotoCard = ({ 
  photo,
  rank,
  onDelete,
  onReanalyze,
  onFavorite,
  onCompare
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e?.targetTouches?.[0]?.clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e?.targetTouches?.[0]?.clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe || isRightSwipe) {
      setShowDetails(!showDetails);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8) return '#10B981';
    if (score >= 6) return '#9333EA';
    if (score >= 4) return '#F59E0B';
    return '#EF4444';
  };

  const getRankBadgeColor = (rank) => {
    if (rank === 1) return 'bg-[#FFD700] text-[#1F2937]';
    if (rank === 2) return 'bg-[#C0C0C0] text-[#1F2937]';
    if (rank === 3) return 'bg-[#CD7F32] text-white';
    return 'bg-gray-200 text-gray-600';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: rank * 0.05 }}
      className="bg-card rounded-xl border border-border overflow-hidden shadow-sm active:shadow-md transition-shadow"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="flex gap-3 p-3">
        {/* Photo Section - Compact */}
        <div className="relative w-24 h-32 flex-shrink-0 rounded-lg overflow-hidden bg-muted">
          <Image
            src={photo?.image}
            alt={photo?.imageAlt}
            className="w-full h-full object-cover"
          />
          
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: rank * 0.05 + 0.1, type: "spring", stiffness: 180, damping: 15 }}
            className={`absolute top-1 left-1 ${getRankBadgeColor(rank)} px-2 py-0.5 rounded-md font-bold text-xs`}
          >
            #{rank}
          </motion.div>

          {photo?.isFavorite && (
            <motion.div 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: rank * 0.05 + 0.15, type: "spring", stiffness: 180, damping: 15 }}
              className="absolute top-1 right-1 bg-purple-600/90 backdrop-blur-sm p-1 rounded-md"
            >
              <Icon name="Heart" size={12} color="white" fill="white" />
            </motion.div>
          )}
        </div>

        {/* Info Section - Compact */}
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center text-lg font-bold"
                style={{ 
                  backgroundColor: `${getScoreColor(photo?.overallScore)}20`,
                  color: getScoreColor(photo?.overallScore)
                }}
              >
                {photo?.overallScore?.toFixed(1)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-foreground">Overall Score</div>
                <div className="flex items-center gap-0.5 mt-0.5">
                  {[...Array(5)]?.map((_, i) => (
                    <Icon
                      key={i}
                      name="Star"
                      size={10}
                      color={i < Math.floor(photo?.overallScore / 2) ? "#FFD700" : "var(--color-muted)"}
                      fill={i < Math.floor(photo?.overallScore / 2) ? "#FFD700" : "none"}
                    />
                  ))}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowDetails(!showDetails)}
                iconName={showDetails ? "ChevronUp" : "ChevronDown"}
                className="h-8 w-8 flex-shrink-0"
              />
            </div>

            {showDetails && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-1.5 mb-2 pt-2 border-t border-border"
              >
                {photo?.criteria?.map((criterion, index) => (
                  <div key={index} className="space-y-0.5">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-medium text-foreground">{criterion?.name}</span>
                      <span 
                        className="text-[10px] font-semibold"
                        style={{ color: getScoreColor(criterion?.score) }}
                      >
                        {criterion?.score?.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-full h-1 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full transition-all duration-500"
                        style={{
                          width: `${(criterion?.score / 10) * 100}%`,
                          backgroundColor: getScoreColor(criterion?.score)
                        }}
                      />
                    </div>
                  </div>
                ))}
              </motion.div>
            )}
          </div>

          {/* Action Buttons - Touch Optimized */}
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onFavorite(photo?.id)}
              iconName="Heart"
              className="flex-1 h-9 text-xs px-2"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onCompare(photo?.id)}
              iconName="ArrowLeftRight"
              className="flex-1 h-9 text-xs px-2"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onReanalyze(photo?.id)}
              iconName="RefreshCw"
              className="flex-1 h-9 text-xs px-2"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(photo?.id)}
              iconName="Trash2"
              className="h-9 w-9 text-error hover:text-error hover:border-error flex-shrink-0"
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default RankedPhotoCard;